<template>
  <div
      class="fullscreen-poll-container"
      :style="fullScreenContainerStyles"
      :key="item.id"
  >
    <!-- Title Section -->
    <div class="title-section" :style="titleSectionStyles">
      <span class="title-text" :style="titleStyles">
        {{ item.dsc_titulo_enquete }}
      </span>
      <!-- Separator Line -->
      <div class="title-separator" :style="titleSeparatorStyles"></div>
    </div>

    <!-- Poll Content Section -->
    <div class="poll-content" :style="pollContentStyles">
      <div class="options-container" :style="optionsContainerStyles">
        <!-- First Option -->
        <div class="option-wrapper" :style="optionWrapperStyles">
          <div v-if="!hideOptionTitle" class="option-text" :style="optionTextStyles">
            {{ leftOption.dsc_titulo }}
          </div>
          <transition name="results-transition" mode="out-in">
            <div v-if="item.flg_exibir_resultado" key="with-results" class="progress-with-percentage" :style="progressWithPercentageStyles">
              <div class="progress-container" :style="progressContainerStyles">
                <div class="progress-bar" :style="getProgressBarStyles(leftOption, 'left')">
                  <div class="progress-fill" :style="progressFillStyles"></div>
                </div>
              </div>
              <transition name="percentage-slide" appear>
                <div class="percentage-text" :style="percentageStyles">
                  {{ animatedLeftPercentage }}%
                </div>
              </transition>
            </div>
            <!-- Show placeholder when results are hidden -->
            <div v-else key="without-results" class="no-results-placeholder" :style="noResultsPlaceholderStyles">
              <div class="placeholder-text" :style="placeholderTextStyles">
                Vote para ver os resultados
              </div>
            </div>
          </transition>
        </div>

        <!-- Second Option -->
        <div class="option-wrapper" :style="optionWrapperStyles">
          <div v-if="!hideOptionTitle" class="option-text" :style="optionTextStyles">
            {{ rightOption.dsc_titulo }}
          </div>
          <transition name="results-transition" mode="out-in">
            <div v-if="item.flg_exibir_resultado" key="with-results" class="progress-with-percentage" :style="progressWithPercentageStyles">
              <div class="progress-container" :style="progressContainerStyles">
                <div class="progress-bar" :style="getProgressBarStyles(rightOption, 'right')">
                  <div class="progress-fill" :style="progressFillStyles"></div>
                </div>
              </div>
              <transition name="percentage-slide" appear>
                <div class="percentage-text" :style="percentageStyles">
                  {{ animatedRightPercentage }}%
                </div>
              </transition>
            </div>
            <!-- Show placeholder when results are hidden -->
            <div v-else key="without-results" class="no-results-placeholder" :style="noResultsPlaceholderStyles">
              <div class="placeholder-text" :style="placeholderTextStyles">
                Vote para ver os resultados
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <!-- Footer Attribution -->
    <div class="footer-attribution" :style="footerAttributionStyles">
      Participe em gshow.com/gnt
    </div>
  </div>
</template>

<script>
export default {
  props: {
    item: Object,
    posicaoEnquete: String,
    hideTitle: Boolean,
    hideOptionTitle: Boolean,
    isVerticalBar: Boolean,
  },

  data() {
    return {
      saiaJustaTheme: {
        colors: {
          containerBg: "rgb(188,153,200)",
          titleColor: "#631C43",
          optionTextColor: "#FBF9EA",
          percentageColor: "#FBF9EA",
          progressBarBg: "#631C43",
          progressFillBg: "rgb(250,247,232)",
          footerTextColor: "#631C43",
          separatorColor: "#631C43",
          gradientStart: "#631C43",
          gradientEnd: "#FBF9EA",
        },
        typography: {
          titleFont: "Fields Regular",
          optionFont: "Fields Regular",
          footerFont: "Fields Regular",
        },
      },
      animatedLeftPercentage: 0,
      animatedRightPercentage: 0,
      animatedProgressLeft: 0,
      animatedProgressRight: 0,
      animationDuration: 1500,
      previousResultsVisible: false,
    };
  },

  computed: {
    currentTheme() {
      return this.saiaJustaTheme;
    },

    leftOption() {
      return this.item?.enquete_opcoes?.[0] || { dsc_titulo: '', resultado: 0 };
    },

    rightOption() {
      return this.item?.enquete_opcoes?.[1] || { dsc_titulo: '', resultado: 0 };
    },

    fullScreenContainerStyles() {
      return {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100vw',
        height: '100vh',
        backgroundColor: this.currentTheme.colors.containerBg,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '80px 120px',
        boxSizing: 'border-box',
        zIndex: 9999,
      };
    },

    titleSectionStyles() {
      return {
        width: '100%',
        maxWidth: '1200px',
        textAlign: 'center',
        marginBottom: '80px',
        padding: '0 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      };
    },

    titleStyles() {
      return {
        color: this.currentTheme.colors.titleColor,
        fontFamily: `${this.currentTheme.typography.titleFont} !important`,
        fontSize: '4.5rem',
        fontWeight: '400',
        lineHeight: '1.2',
        display: 'block',
        wordWrap: 'break-word',
        textAlign: 'center',
        marginBottom: '40px',
      };
    },

    titleSeparatorStyles() {
      return {
        width: '100%',
        maxWidth: '1200px',
        height: '2px',
        backgroundColor: this.currentTheme.colors.separatorColor,
        margin: '0 auto',
      };
    },

    pollContentStyles() {
      return {
        width: '100%',
        maxWidth: '1400px',
        flexDirection: 'column',
        flex: '1',
      };
    },

    optionsContainerStyles() {
      return {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        width: '100%',
        gap: '40px',
        flexDirection: 'column',
      };
    },

    optionWrapperStyles() {
      return {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        flex: '1',
        maxWidth: '800px',
        width: '100%',
      };
    },

    progressWithPercentageStyles() {
      return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        width: '100%',
        gap: '40px',
        minHeight: '80px',
        maxHeight: '80px',
        height: '80px',
      };
    },

    optionTextStyles() {
      return {
        color: this.currentTheme.colors.optionTextColor,
        fontFamily: `${this.currentTheme.typography.optionFont} !important`,
        fontSize: '2.8rem',
        fontWeight: '400',
        textAlign: 'center',
        wordWrap: 'break-word',
        lineHeight: '1.3',
        minHeight: '80px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      };
    },

    progressContainerStyles() {
      return {
        display: 'flex',
        alignItems: 'center',
      };
    },

    progressFillStyles() {
      return {
        width: '100%',
        height: '100%',
        borderRadius: '0px',
      };
    },

    percentageStyles() {
      return {
        color: this.currentTheme.colors.percentageColor,
        fontFamily: `${this.currentTheme.typography.optionFont} !important`,
        fontSize: '4.5rem',
        fontWeight: '400',
        textAlign: 'left',
        minHeight: '80px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minWidth: '120px',
        flexShrink: 0,
        transition: 'all 0.1s ease-out',
      };
    },

    footerAttributionStyles() {
      return {
        position: 'absolute',
        bottom: '40px',
        right: '350px',
        color: this.currentTheme.colors.footerTextColor,
        fontFamily: `${this.currentTheme.typography.footerFont} !important`,
        fontSize: '1.8rem',
        fontWeight: '400',
      };
    },

    noResultsPlaceholderStyles() {
      return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '80px',
        minHeight: '80px',
        maxHeight: '80px',
        gap: '40px',
      };
    },

    placeholderTextStyles() {
      return {
        color: this.currentTheme.colors.optionTextColor,
        fontFamily: `${this.currentTheme.typography.optionFont} !important`,
        fontSize: '2.2rem',
        fontWeight: '400',
        textAlign: 'center',
        fontStyle: 'italic',
        opacity: 0.8,
        minWidth: '120px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      };
    },
  },

  watch: {
    'item.flg_exibir_resultado': {
      handler(newVal) {
        if (newVal && !this.previousResultsVisible) {
          this.animateValue('animatedLeftPercentage', 0, this.leftOption.resultado || 0);
          this.animateValue('animatedProgressLeft', 0, this.leftOption.resultado || 0);
          this.animateValue('animatedRightPercentage', 0, this.rightOption.resultado || 0);
          this.animateValue('animatedProgressRight', 0, this.rightOption.resultado || 0);
        }
        else if (!newVal) {
          this.animatedLeftPercentage = 0;
          this.animatedRightPercentage = 0;
          this.animatedProgressLeft = 0;
          this.animatedProgressRight = 0;
        }
        this.previousResultsVisible = newVal;
      },
      immediate: true,
    },

    'leftOption.resultado': {
      handler(newVal, oldVal) {
        if (this.item.flg_exibir_resultado && this.previousResultsVisible) {
          this.animateValue('animatedLeftPercentage', oldVal || 0, newVal || 0);
          this.animateValue('animatedProgressLeft', oldVal || 0, newVal || 0);
        }
      },
    },
    'rightOption.resultado': {
      handler(newVal, oldVal) {
        if (this.item.flg_exibir_resultado && this.previousResultsVisible) {
          this.animateValue('animatedRightPercentage', oldVal || 0, newVal || 0);
          this.animateValue('animatedProgressRight', oldVal || 0, newVal || 0);
        }
      },
    },
  },

  methods: {
    animateValue(propertyName, fromValue, toValue) {
      const startTime = Date.now();
      const difference = toValue - fromValue;

      if (this[propertyName + 'Animation']) {
        cancelAnimationFrame(this[propertyName + 'Animation']);
      }

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / this.animationDuration, 1);
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);

        this[propertyName] = Math.round(fromValue + (difference * easeOutQuart));

        if (progress < 1) {
          this[propertyName + 'Animation'] = requestAnimationFrame(animate);
        } else {
          this[propertyName] = toValue;
          delete this[propertyName + 'Animation'];
        }
      };

      this[propertyName + 'Animation'] = requestAnimationFrame(animate);
    },

    getProgressBarStyles(option, side) {
      if (!this.item.flg_exibir_resultado) {
        return {
          width: '0px',
          height: '0px',
          background: 'transparent',
          display: 'none',
        };
      }

      const animatedPercentage = side === 'left' ? this.animatedProgressLeft : this.animatedProgressRight;
      const maxWidth = 600;
      const calculatedWidth = Math.max((animatedPercentage / 100) * maxWidth, animatedPercentage > 0 ? 60 : 0);

      return {
        width: `${calculatedWidth}px`,
        height: '60px',
        background: animatedPercentage > 0 ? `linear-gradient(to right, ${this.currentTheme.colors.gradientStart} 40%, ${this.currentTheme.colors.gradientEnd} 100%)` : 'transparent',
        borderRadius: '0px',
        overflow: 'hidden',
        position: 'relative',
        transition: 'width 1.0s cubic-bezier(0.25, 0.46, 0.45, 0.94), background 1.0s ease',
      };
    },
  },

  mounted() {
    if (this.item?.flg_exibir_resultado) {
      this.animatedLeftPercentage = this.leftOption.resultado || 0;
      this.animatedRightPercentage = this.rightOption.resultado || 0;
      this.animatedProgressLeft = this.leftOption.resultado || 0;
      this.animatedProgressRight = this.rightOption.resultado || 0;
      this.previousResultsVisible = true;
    } else {
      this.animatedLeftPercentage = 0;
      this.animatedRightPercentage = 0;
      this.animatedProgressLeft = 0;
      this.animatedProgressRight = 0;
      this.previousResultsVisible = false;
    }
  },

  beforeDestroy() {
    Object.keys(this.$data).forEach(key => {
      if (key.endsWith('Animation') && this[key]) {
        cancelAnimationFrame(this[key]);
      }
    });
  },
};
</script>

<style lang="scss" scoped>
@font-face {
  font-family: "Fields Regular";
  src: url("@/assets/SaiaJusta/fonts/fields_regular.otf") format("opentype");
}

@font-face {
  font-family: "Fields Medium";
  src: url("@/assets/SaiaJusta/fonts/fields_medium.otf") format("opentype");
}

.fullscreen-poll-container {
  user-select: none;

  * {
    box-sizing: border-box;
  }
}

.title-section {
  .title-text {
    word-break: break-word;
    hyphens: auto;
  }
}

.options-container {
  .option-wrapper {
    .option-text {
      word-break: break-word;
      hyphens: auto;
    }

    .progress-bar {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      transition: width 1.0s cubic-bezier(0.25, 0.46, 0.45, 0.94),
      background 1.0s ease,
      box-shadow 0.6s ease;

      &:hover {
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
      }
    }

    .progress-fill {
      transform-origin: left center;
    }
  }
}

.footer-attribution {
  opacity: 0.9;
}

.no-results-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 80px;
  height: 80px;
  max-height: 80px;
}

.placeholder-text {
  word-break: break-word;
  hyphens: auto;
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.results-transition-enter-active,
.results-transition-leave-active {
  transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  will-change: transform, opacity;
}

.results-transition-enter-from {
  opacity: 0;
  transform: translateY(25px) scale(0.94);
}

.results-transition-leave-to {
  opacity: 0;
  transform: translateY(-25px) scale(0.94);
}

.percentage-slide-enter-active {
  transition: all 1.0s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transition-delay: 0.4s;
}

.percentage-slide-enter-from {
  opacity: 0;
  transform: translateX(40px) scale(0.7);
}

.progress-with-percentage,
.no-results-placeholder {
  position: relative;
  min-height: 80px;
  max-height: 80px;
  box-sizing: border-box;
}

@media (max-width: 1600px) {
  .fullscreen-poll-container {
    padding: 60px 80px !important;
  }

  .title-text {
    font-size: 4rem !important;
    margin-bottom: 30px !important;
  }

  .title-separator {
    max-width: 700px !important;
  }

  .option-text {
    font-size: 2.4rem !important;
  }

  .percentage-text {
    font-size: 4rem !important;
  }

  .placeholder-text {
    font-size: 2rem !important;
  }

  .options-container {
    gap: 60px !important;
  }

  .progress-with-percentage {
    gap: 30px !important;
  }
}

@media (max-width: 1200px) {
  .fullscreen-poll-container {
    padding: 40px 60px !important;
  }

  .title-text {
    font-size: 3.5rem !important;
  }

  .option-text {
    font-size: 2rem !important;
  }

  .percentage-text {
    font-size: 3.5rem !important;
  }

  .placeholder-text {
    font-size: 1.8rem !important;
  }

  .options-container {
    gap: 50px !important;
  }

  .progress-with-percentage {
    gap: 25px !important;
  }

  .footer-attribution {
    font-size: 1.5rem !important;
  }
}

@media (max-width: 900px) {
  .options-container {
    flex-direction: column !important;
    gap: 60px !important;
  }

  .option-wrapper {
    max-width: 600px !important;
  }

  .title-section {
    margin-bottom: 60px !important;
  }

  .title-separator {
    max-width: 500px !important;
  }

  .progress-with-percentage {
    gap: 20px !important;
  }
}

@media (max-width: 600px) {
  .fullscreen-poll-container {
    padding: 30px 40px !important;
  }

  .title-text {
    font-size: 2.5rem !important;
    margin-bottom: 20px !important;
  }

  .title-separator {
    max-width: 100% !important;
  }

  .option-text {
    font-size: 1.6rem !important;
  }

  .percentage-text {
    font-size: 2.8rem !important;
    min-width: 100px !important;
  }

  .placeholder-text {
    font-size: 1.4rem !important;
  }

  .progress-with-percentage {
    gap: 15px !important;
  }

  .footer-attribution {
    font-size: 1.2rem !important;
    bottom: 20px !important;
    right: 30px !important;
  }
}
</style>
